import { Stack, Typography, Tooltip, Button, Box, useTheme, useMediaQuery } from "@mui/material"
import { DisplayBoxBase } from "../../../components/shared/StyledComponents"
import { MessageCirclePlus, CalendarPlus, Phone, Timer, CircleCheck } from "lucide-react"
import { MeetingDisplay } from "../../meeting/components/MeetingDisplay"
import { CalendlyScheduler } from "../../meeting"
import { getAdvisorMeetings } from "../../../core/services/gutcheck-api.service"
import { useEffect, useState, useMemo } from "react"
import { IScheduler, IMeeting, IAdvisor, IProject, ISurveyLink, SchedulerType, IViewpoint, TouchpointType } from "../../../types"
import ClientProvider from "../../../modules/ClientProvider"
import Loading from "../../../components/Loading"
import { LoginButton } from "../../../components/buttons/login-button"
import { useSchedulers } from "../../../hooks"

export const StreamlinedOnboarding = ({ advisor, project, surveyLink }: { advisor: IAdvisor, project: IProject, surveyLink: ISurveyLink }) => {

    const [meetings, setMeetings] = useState<IMeeting[]>([]);
    const [isCalendlyOpen, setIsCalendlyOpen] = useState<boolean>(false);
    const [meetingCreated, setMeetingCreated] = useState<boolean>(false);
    const [activeScheduler, setActiveScheduler] = useState<IScheduler | null>(null);

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    // Memoize the types array to prevent infinite loops
    const schedulerTypes = useMemo(() => [
        SchedulerType.ONBOARDING
    ], []);

    // Use the new hook to fetch schedulers
    const { schedulers, loading: schedulersLoading } = useSchedulers({
        projectTag: project?.tag || "",
        types: schedulerTypes,
        isPublic: true,
        enabled: true
    });

    console.log(schedulers);

    const onboardingScheduler = schedulers[SchedulerType.ONBOARDING];

    useEffect(() => {
        if (advisor && project) {
            fetchMeetings();
        }
    }, [advisor?.public_id, project?.tag]);

    const fetchMeetings = async () => {
        if (advisor) {
            const { data, error } = await getAdvisorMeetings(advisor?.public_id);
            if (data) {
                setMeetings(data.meetings);
            }
        }
    }

    const handleMeetingCreated = async () => {
        setMeetingCreated(true);
        setIsCalendlyOpen(false);
        window.location.href = window.location.href.replace("/schedule", "/start?scheduled=true");

        setTimeout(async () => {
            fetchMeetings();
        }, 2000);
    }


    const handleBookOnboarding = async () => {
        if (!onboardingScheduler) {
            console.warn("Onboarding scheduler not available");
            return;
        }
        setActiveScheduler(onboardingScheduler);
        launchCalendly();
    }

    const handleSetOpen = async (state: boolean) => {
        if (state) {
            setIsCalendlyOpen(true);
        } else {
            setIsCalendlyOpen(false);
            setActiveScheduler(null);
        }
    }

    const launchCalendly = () => {
        setIsCalendlyOpen(true);
    }

    if (!project) {
        return <Loading />;
    }

    const advisorLink = advisor?.latest_survey_url || advisor?.link;
    const name = advisor?.user?.profile?.name || advisor?.name;
    const email = advisor?.user?.email_address || advisor?.email_address;

    const onboardingMeeting = meetings?.find(meeting => meeting.scheduler?.type === "onboarding");
    if (onboardingMeeting) {
        window.location.href = window.location.href.replace("/schedule", "/start?scheduled=true");
    }

    console.log(advisorLink);

    // Prepare box and action content for each step


    let extraSections = null;

    let onboardingSection = null;
    if (onboardingMeeting) {
        onboardingSection = (
            <Box display="flex" className="display-box" flexDirection={{ xs: "column", md: "row" }} sx={{ justifyContent: 'space-between', alignItems: 'center', gap: '20px' }} mt={2} mb={4}>
                <Box>
                    <Typography variant="body1" fontWeight="bold" fontSize={"1rem"}>Get ready for your call</Typography>
                    <Typography variant="body1" sx={{ fontSize: '0.9rem' }}>You have an upcoming call with an advisor specialist who can help determine if you're a good match for {project?.name}.</Typography>
                </Box>
                <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 460px" } }}>
                    <MeetingDisplay meeting={onboardingMeeting} advisor={advisor} size="normal" />
                </Box>
            </Box>
        );
    } else {
        onboardingSection = (
            <Box>

                    {onboardingScheduler ? (
                        <Box sx={{ border: '1px solid #c9c9c9', borderRadius: '10px' }}>
                            <CalendlyScheduler project={project} scheduler={onboardingScheduler} advisor={advisor} surveyLink={surveyLink} onMeetingCreated={handleMeetingCreated} name={name} email={email} inline={true} open={true} setOpen={handleSetOpen} />
                        </Box>
                    ) : (
                        <Button variant="outlined" disabled={true} color="primary" sx={{ width: '100%', whiteSpace: 'nowrap' }}><Phone size={14} />&nbsp;&nbsp;Not Available</Button>
                    )}
                
            </Box>
        );
    }

    if (isDesktop) {
        // Desktop: two rows, boxes then actions
        return (
            <>
                {extraSections}
                {onboardingSection}
                {isCalendlyOpen && activeScheduler && <CalendlyScheduler project={project} scheduler={activeScheduler} advisor={advisor} surveyLink={surveyLink} onMeetingCreated={handleMeetingCreated} name={name} email={email} inline={false} open={isCalendlyOpen} setOpen={handleSetOpen} />}
            </>
        );
    } else {
        // Mobile: stack each box with its action
        return (
            <>
                {extraSections}
                {onboardingSection}
                {isCalendlyOpen && activeScheduler && <CalendlyScheduler project={project} scheduler={activeScheduler} advisor={advisor} surveyLink={surveyLink} onMeetingCreated={handleMeetingCreated} name={name} email={email} inline={false} open={isCalendlyOpen} setOpen={handleSetOpen} />}
            </>
        );
    }
}